<template>
  <div :id="modalId" class="modal overflow-y-auto flex" tabindex="-1" aria-hidden="true" :class="{ show: isOpen }">
    <div class="modal-dialog">
      <div class="modal-content">
        <!-- Modal Header -->
        <div class="modal-header px-5 py-3 border-b border-gray-200 dark:border-dark-5">
          <h2 class="font-medium text-base">
            {{ $t("views.medication.quick_create.new_family") }}
          </h2>
        </div>

        <!-- Modal Body -->
        <div class="modal-body px-5 py-6">
          <!-- Name Input -->
          <div>
            <label for="quick-family-name" class="form-label font-semibold">
              {{ $t("views.medication.quick_create.name") }}
            </label>
            <div class="input-group">
              <input
                id="quick-family-name"
                type="text"
                class="form-control w-full bg-theme-32"
                :class="validationError && !family.name.trim() ? 'border border-theme-6' : ''"
                :placeholder="$t('views.medication.quick_create.name')"
                v-model="family.name"
                maxlength="110"
                @input="clearValidationError"
              />
            </div>
          </div>

          <!-- Description Input -->
          <div class="mt-5">
            <label for="quick-family-description" class="form-label font-semibold">
              {{ $t("views.medication.quick_create.description") }}
            </label>
            <div class="input-group">
              <textarea
                id="quick-family-description"
                rows="5"
                class="form-control w-full bg-theme-32"
                :placeholder="$t('views.medication.quick_create.description')"
                v-model="family.description"
                maxlength="2000"
              ></textarea>
            </div>
          </div>

          <!-- Error messages -->
          <div v-if="validationError" class="text-theme-6 mt-3 text-sm font-semibold">
            {{ validationError }}
          </div>
          <div v-if="apiError" class="text-theme-6 mt-3 text-sm font-semibold">
            {{ apiError }}
          </div>
        </div>

        <!-- Modal Footer -->
        <div
          class="modal-footer px-5 py-3 border-t border-gray-200 dark:border-dark-5 text-right flex justify-end gap-3"
        >
          <button type="button" @click="close" class="btn btn-outline-secondary w-24 mr-2" :disabled="loading">
            {{ $t("views.cancel") }}
          </button>
          <button
            type="button"
            @click="save"
            class="btn btn-primary w-24 flex items-center justify-center"
            :disabled="loading"
          >
            <LoadingIcon v-if="loading" icon="three-dots" class="w-4 h-4 ml-2" />
            <span v-else>{{ $t("views.save") }}</span>
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from "vue";
import MedicationService from "@/services/medications.service";
import Toastify from "toastify-js";

export default defineComponent({
  name: "FamilyQuickCreateModal",
  props: {
    modalId: {
      type: String,
      default: "family-quick-create-modal",
    },
  },
  emits: ["created"],
  data() {
    return {
      isOpen: false,
      loading: false,
      family: {
        name: "",
        description: "",
      },
      validationError: "",
      apiError: "",
      lang_key: localStorage.lang ? localStorage.lang : navigator.language.split("-")[0],
    };
  },
  created() {
    this.resetForm();
  },
  methods: {
    resetForm() {
      this.family = {
        name: "",
        description: "",
      };
      this.validationError = "";
      this.apiError = "";
    },
    show() {
      this.resetForm();
      this.isOpen = true;
      cash("#" + this.modalId).modal("show");
    },
    close() {
      this.isOpen = false;
      cash("#" + this.modalId).modal("hide");
    },
    clearValidationError() {
      this.validationError = "";
    },
    async save() {
      this.validationError = "";
      this.apiError = "";

      if (!this.family.name || !this.family.name.trim()) {
        this.validationError = "El nombre no es válido";
        return;
      }

      this.loading = true;

      try {
        const response = await MedicationService.saveFamily(this.family, this.lang_key);

        let newFamilyId = null;
        if (response && response.data && response.data.length > 0) {
          newFamilyId = response.data[0].id;
        } else if (response && response.id) {
          newFamilyId = response.id;
        }

        // Emit event to parent with the newly created family details
        this.$emit("created", {
          id: newFamilyId || response.id,
          name: this.family.name,
          description: this.family.description,
        });

        this.showToast();
        this.close();
      } catch (error) {
        console.error("Error creating medication family via quick modal:", error);
        this.apiError = error?.response?.data?.message || error?.message || "Error al guardar la familia.";
      } finally {
        this.loading = false;
      }
    },
    showToast() {
      const customNode = cash(`
        <div class="toastify-content flex">
          <div class="text-theme-9">✓</div>
          <div class="ml-4 mr-4">
            <div class="font-medium">Guardado con éxito.</div>
          </div>
        </div>
      `)[0];
      Toastify({
        node: customNode,
        duration: 3000,
        gravity: "top",
        position: "right",
        close: true,
      }).showToast();
    },
  },
});
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
